import React, { useEffect, useMemo, useState } from "react";
import {
  FileDownload as FileDownloadIcon,
  ClearAll as ClearAllIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
} from "@mui/icons-material";

import * as XLSX from "xlsx";

import pestReportService from "../api/pestReportService";
import {
  formatServiceDateSlash,
  formatMonthYearLabel,
  getMonthYearKey,
  getVisitMonthYearKey,
} from "../utils/dateUtils";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const PRIMARY = "#1975d1";
const BORDER = "#e5e7eb";
const TEXT = "#111827";
const TEXT_SOFT = "#6b7280";

const CONTROL_HEIGHT = 40;
const CONTROL_RADIUS = 8;

const SUPPORT_COLS = [
  { key: "TICKET", label: "Ticket" },
  { key: "NEBULIZACION", label: "Nebulización" },
  { key: "MICROINYECCION", label: "Microinyección" },
  { key: "MOSCA", label: "Mosca" },
  { key: "ABEJA", label: "Abeja" },
  { key: "EQUIPO", label: "Equipo" },
  { key: "CHOQUE", label: "Plan de Choque" },
  { key: "SUPERVISION", label: "Seguimiento" },
  { key: "EVENTUAL", label: "Eventual" },
  { key: "OTRO", label: "Otro" },
];

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const formatTime = (iso) => {
  if (!iso) return null;

  const d = new Date(iso);

  return `${String(d.getHours()).padStart(2, "0")}:${String(
    d.getMinutes()
  ).padStart(2, "0")}`;
};

const calcHours = (s, e) => {
  if (!s || !e) return null;

  const mins = Math.round(
    (new Date(e) - new Date(s)) / 60000
  );

  if (mins <= 0) return null;

  const h = Math.floor(mins / 60);
  const m = mins % 60;

  return `${h}h ${String(m).padStart(2, "0")}m`;
};

const getTechnicianLabel = (event) => {
  const technicians = event?.technicians || [];

  if (technicians.length > 1) {
    return "Múltiples técnicos";
  }

  if (technicians.length === 1) {
    const tech = technicians[0];
    return tech?.name || "Sin asignar";
  }

  return (
    event?.technician?.name ||
    (typeof event?.technician === "string"
      ? event.technician
      : null) ||
    "Sin asignar"
  );
};

const fmtShort = (iso) => formatServiceDateSlash(iso);

const monthLabel = (year, month) => formatMonthYearLabel(year, month, "es-MX");

const getTechName = (t) =>
  t?.name || (typeof t === "string" ? t : null) || "Sin asignar";

const getClientName = (c) =>
  c?.name || (typeof c === "string" ? c : null) || "N/A";

const getBranchName = (b) =>
  b?.name || (typeof b === "string" ? b : null) || "N/A";

const getBranchCity = (b) => b?.city || "N/A";

const getBranchId = (b) =>
  b?._id || (typeof b === "string" ? b : null);

const entityId = (value) => {
  if (!value) return "";
  if (typeof value === "object") return String(value._id || value.id || "");
  return String(value);
};

const getDistributionMonthKey = (event) =>
  getVisitMonthYearKey(event?.pestReport?.entryTime) ||
  getMonthYearKey(event?.date);

const groupByBranch = (events) => {
  const map = new Map();

  const sorted = [...events].sort((a, b) =>
    getBranchName(a.branch).localeCompare(
      getBranchName(b.branch),
      "es",
      { sensitivity: "base" }
    )
  );

  for (const ev of sorted) {
    const branchId = getBranchId(ev.branch) || ev._id;

    const clientId =
      ev.client?._id || ev.client || "";

    const rowKey = `${branchId}__${clientId}`;

    if (!map.has(rowKey)) {
      map.set(rowKey, {
        rowKey,
        branchName: getBranchName(ev.branch),
        clientName: getClientName(ev.client),
        city: getBranchCity(ev.branch),
        technician: getTechnicianLabel(ev),
        entryTime: formatTime(
          ev.pestReport?.entryTime
        ),
        exitTime: formatTime(
          ev.pestReport?.exitTime
        ),
        totalHours: calcHours(
          ev.pestReport?.entryTime,
          ev.pestReport?.exitTime
        ),
        notes: ev.notes || "",
        mipDates: [],
        typeFirstDate: {},
      });
    }

    const row = map.get(rowKey);

    if (ev.type === "MIP") {
      row.mipDates.push(fmtShort(ev.date));
    } else {
      if (!row.typeFirstDate[ev.type]) {
        row.typeFirstDate[ev.type] =
          fmtShort(ev.date);
      }
    }

    if (
      ev.notes &&
      !row.notes.includes(ev.notes)
    ) {
      row.notes = row.notes
        ? `${row.notes} / ${ev.notes}`
        : ev.notes;
    }
  }

  return [...map.values()].sort((a, b) =>
    a.branchName.localeCompare(
      b.branchName,
      "es",
      { sensitivity: "base" }
    )
  );
};

const maxMipCount = (groupedData) =>
  groupedData.reduce(
    (max, row) =>
      Math.max(max, row.mipDates.length),
    0
  );

// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────

const S = {
  page: {
    width: "calc(100% + (var(--app-page-padding, 24px) * 2))",
    minHeight: "calc(100vh - var(--app-nav-height, 56px))",
    margin: "calc(var(--app-page-padding, 24px) * -1)",
    padding: 16,
    boxSizing: "border-box",
    background: "#fff",
    display: "flex",
    flexDirection: "column",
    gap: 0,
    fontFamily:
      "'Inter', 'Segoe UI', system-ui, sans-serif",
  },

  card: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${BORDER}`,
    borderRadius: 12,
    overflow: "hidden",
    background: "#fff",
    minHeight: 0,
  },

  header: {
    padding: "20px 24px 16px",
    borderBottom: `1px solid ${BORDER}`,
    display: "flex",
    flexDirection: "column",
    gap: 14,
    flexShrink: 0,
  },

  headerTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
  },

  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
  },

  titleWrap: {
    minWidth: 0,
  },

  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    color: TEXT,
    letterSpacing: "-0.02em",
    lineHeight: 1.2,
  },

  subtitle: {
    margin: "3px 0 0",
    fontSize: 13,
    color: TEXT_SOFT,
    fontWeight: 400,
  },

  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    flexShrink: 0,
  },

  checkboxWrap: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    fontSize: 13,
    fontWeight: 500,
    color: TEXT_SOFT,
    cursor: "pointer",
    userSelect: "none",
  },

  exportBtn: {
    height: CONTROL_HEIGHT,
    padding: "0 16px",
    borderRadius: CONTROL_RADIUS,
    border: "none",
    background: PRIMARY,
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  filterRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },

  filterCount: {
    marginLeft: "auto",
    fontSize: 13,
    fontWeight: 600,
    color: TEXT_SOFT,
    whiteSpace: "nowrap",
  },

  select: {
    height: CONTROL_HEIGHT,
    minWidth: 150,
    borderRadius: CONTROL_RADIUS,
    border: `1px solid ${BORDER}`,
    background: "#fff",
    padding: "0 32px 0 12px",
    fontSize: 14,
    color: TEXT,
    outline: "none",
    cursor: "pointer",
    appearance: "none",
    boxSizing: "border-box",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2.5'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 10px center",
  },

  clearBtn: {
    width: CONTROL_HEIGHT,
    height: CONTROL_HEIGHT,
    borderRadius: CONTROL_RADIUS,
    border: `1px solid ${BORDER}`,
    background: "#fff",
    color: TEXT_SOFT,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
  },

  tableSection: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },

  tableWrap: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    overflow: "hidden",
    borderRadius: 16,
    border: "1px solid #e2e8f0",
    background: "#fff",
    boxSizing: "border-box",
    contain: "inline-size",
  },

  scroll: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    overflowX: "auto",
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch",
    contain: "inline-size",
  },

  table: {
    width: "max-content",
    minWidth: "100%",
    tableLayout: "fixed",
    borderCollapse: "collapse",
  },

  th: {
    background: PRIMARY,
    color: "#fff",
    padding: "10px 8px",
    fontSize: 9,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    textAlign: "left",
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: 1.25,
    verticalAlign: "bottom",
  },

  thCompact: {
    width: 72,
    minWidth: 72,
    maxWidth: 72,
    textAlign: "center",
    padding: "8px 4px",
    fontSize: 8,
  },

  thBranch: {
    width: 130,
    minWidth: 130,
    maxWidth: 130,
    position: "sticky",
    left: 0,
    zIndex: 3,
  },

  td: {
    padding: "6px 8px",
    fontSize: 11,
    color: "#64748b",
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: 1.35,
    verticalAlign: "top",
  },

  tdCompact: {
    width: 72,
    minWidth: 72,
    maxWidth: 72,
    padding: "6px 4px",
    fontSize: 11,
    textAlign: "center",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  },

  tdBranch: {
    padding: "6px 8px",
    fontSize: 11,
    fontWeight: 700,
    color: "#0f172a",
    borderBottom: "1px solid #f1f5f9",
    width: 130,
    minWidth: 130,
    maxWidth: 130,
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: 1.35,
    position: "sticky",
    left: 0,
    zIndex: 1,
    boxShadow: "2px 0 6px rgba(15, 23, 42, 0.06)",
  },

  tdCompany: {
    width: 88,
    minWidth: 88,
    maxWidth: 88,
  },

  tdTime: {
    width: 52,
    minWidth: 52,
    maxWidth: 52,
    whiteSpace: "nowrap",
    textAlign: "center",
  },

  tdNotes: {
    width: 160,
    minWidth: 160,
    maxWidth: 200,
    whiteSpace: "normal",
    wordBreak: "break-word",
  },

  empty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "80px 24px",
    color: "#94a3b8",
  },

  loader: {
    width: "calc(100% + (var(--app-page-padding, 24px) * 2))",
    minHeight: "calc(100vh - var(--app-nav-height, 56px))",
    margin: "calc(var(--app-page-padding, 24px) * -1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#fff",
  },

  spinner: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    border: "3px solid #e5e7eb",
    borderTopColor: PRIMARY,
    animation: "spin 0.7s linear infinite",
  },
};

const dateBadge = (dateStr) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  height: 20,
  borderRadius: 5,
  padding: "0 5px",
  background: "#dbeafe",
  color: "#1d4ed8",
  fontSize: 10,
  fontWeight: 700,
  whiteSpace: "nowrap",
});

const mipBadge = (dateStr) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  height: 20,
  borderRadius: 5,
  padding: "0 5px",
  background: "#ede9fe",
  color: "#6d28d9",
  fontSize: 10,
  fontWeight: 700,
  whiteSpace: "nowrap",
});

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export default function DistributionReport() {
  const [eventsWithReports, setEventsWithReports] =
    useState([]);

  const [groupedData, setGroupedData] =
    useState([]);

  const [filterByClient, setFilterByClient] =
    useState("");

  const [filterByZone, setFilterByZone] =
    useState("");

  const [filterByMonth, setFilterByMonth] =
    useState("");

  const [includeComments, setIncludeComments] =
    useState(true);

  const [loading, setLoading] =
    useState(true);

  // ───────────────────────────────────────────────────────────────────────────
  // Load
  // ───────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        const allReports =
          await pestReportService.getAllReports();

        const merged = allReports
          .map((report) => {
            const ev =
              report.event && typeof report.event === "object"
                ? report.event
                : null;
            if (!ev || !entityId(ev)) return null;
            return {
              ...ev,
              _id: entityId(ev),
              pestReport: report,
            };
          })
          .filter(Boolean);

        setEventsWithReports(merged);

        const availableMonths = [
          ...new Set(
            merged.map((e) => getDistributionMonthKey(e)).filter(Boolean)
          ),
        ].sort((a, b) =>
          b.localeCompare(a)
        );

        if (availableMonths.length) {
          setFilterByMonth(
            availableMonths[0]
          );
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ───────────────────────────────────────────────────────────────────────────
  // Dynamic options
  // ───────────────────────────────────────────────────────────────────────────

  const monthOptions = useMemo(() => {
    const unique = new Map();

    eventsWithReports.forEach((e) => {
      const value = getDistributionMonthKey(e);
      if (!value) return;

      if (!unique.has(value)) {
        const [yr, mo] = value.split("-").map(Number);
        unique.set(value, {
          value,
          label: monthLabel(yr, mo),
        });
      }
    });

    return [...unique.values()].sort((a, b) =>
      b.value.localeCompare(a.value)
    );
  }, [eventsWithReports]);

  const zones = useMemo(
    () =>
      [
        ...new Set(
          eventsWithReports
            .map((e) =>
              getBranchCity(e.branch)
            )
            .filter((c) => c !== "N/A")
        ),
      ].sort((a, b) =>
        a.localeCompare(b, "es", {
          sensitivity: "base",
        })
      ),
    [eventsWithReports]
  );

  const uniqueClients = useMemo(
    () =>
      [
        ...new Map(
          eventsWithReports.map((e) => {
            const id =
              e.client?._id ||
              e.client;

            return [
              id,
              {
                _id: id,
                name: getClientName(
                  e.client
                ),
              },
            ];
          })
        ).values(),
      ]
        .filter((c) => c._id)
        .sort((a, b) =>
          a.name.localeCompare(
            b.name,
            "es",
            {
              sensitivity: "base",
            }
          )
        ),
    [eventsWithReports]
  );

  // ───────────────────────────────────────────────────────────────────────────
  // Filters
  // ───────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    let f = [...eventsWithReports];

    f = f.filter(
      (e) => e.status !== "SCHEDULED"
    );

    f = f.filter(
      (e) => e.pestReport?.exitTime
    );

    if (filterByClient) {
      f = f.filter(
        (e) =>
          (e.client?._id ||
            e.client) ===
          filterByClient
      );
    }

    if (filterByZone) {
      f = f.filter(
        (e) =>
          getBranchCity(e.branch) ===
          filterByZone
      );
    }

    if (filterByMonth) {
      f = f.filter(
        (e) => getDistributionMonthKey(e) === filterByMonth
      );
    }

    setGroupedData(groupByBranch(f));
  }, [
    eventsWithReports,
    filterByClient,
    filterByZone,
    filterByMonth,
  ]);

  const totalReports =
    groupedData.length;

  const activeSupportCols =
    SUPPORT_COLS.filter((col) =>
      groupedData.some(
        (row) =>
          !!row.typeFirstDate[
            col.key
          ]
      )
    );

  const mipColCount =
    maxMipCount(groupedData);

  const activeMonthLabel =
    monthOptions.find(
      (m) =>
        m.value ===
        filterByMonth
    )?.label ||
    "Todos los meses";

  // ───────────────────────────────────────────────────────────────────────────
  // Export
  // ───────────────────────────────────────────────────────────────────────────

  const handleExport = () => {
    const rows = groupedData.map(
      (row) => {
        const base = {
          "Nombre de Tienda":
            row.branchName.toUpperCase(),

          Empresa:
            row.clientName.toUpperCase(),

          "Hora Entrada":
            row.entryTime || "",

          "Hora Salida":
            row.exitTime || "",

          "Total Horas":
            row.totalHours || "",

          Ciudad: row.city,

          Técnico:
            row.technician,
        };

        row.mipDates.forEach(
          (d, i) => {
            base[`MIP ${i + 1}`] =
              d || "";
          }
        );

        SUPPORT_COLS.forEach(
          (col) => {
            base[col.label] =
              row.typeFirstDate[
                col.key
              ] || "";
          }
        );

        if (includeComments) {
          base["Observaciones"] =
            row.notes || "";
        }

        return base;
      }
    );

    const ws =
      XLSX.utils.json_to_sheet(rows);

    const wb =
      XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      wb,
      ws,
      "Reporte Distribución"
    );

    XLSX.writeFile(
      wb,
      `Reporte_Distribucion_${filterByMonth}.xlsx`
    );
  };

  const hasFilters =
    filterByClient ||
    filterByZone ||
    filterByMonth;

  // ───────────────────────────────────────────────────────────────────────────
  // Loading
  // ───────────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={S.loader}>
        <style>
          {`
            @keyframes spin {
              to {
                transform: rotate(360deg);
              }
            }
          `}
        </style>

        <div style={S.spinner} />
      </div>
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Render
  // ───────────────────────────────────────────────────────────────────────────

  return (
    <div style={S.page}>
      <style>
        {`
          .fil-sel:hover {
            border-color: ${PRIMARY} !important;
          }

          .fil-sel:focus {
            border-color: ${PRIMARY} !important;
            box-shadow: 0 0 0 2px ${PRIMARY}18 !important;
          }

          .export-btn:hover {
            opacity: 0.9;
          }

          .clear-btn:hover {
            border-color: #d1d5db;
            color: ${TEXT};
          }

          .dist-checkbox input {
            accent-color: ${PRIMARY};
            cursor: pointer;
          }

          .tr-row:hover td {
            background: #f8fafc !important;
          }

          .dist-report-table .tr-row:hover td:first-child {
            background: #f8fafc !important;
          }

          .dist-scroll-hint {
            padding: 8px 12px;
            font-size: 11px;
            font-weight: 600;
            color: #64748b;
            border-bottom: 1px solid #f1f5f9;
            background: #fafbfc;
          }

          ::-webkit-scrollbar {
            height: 7px;
          }

          ::-webkit-scrollbar-track {
            background: #f1f5f9;
          }

          ::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 999px;
          }
        `}
      </style>

      <div style={S.card}>
        <div style={S.header}>
          <div style={S.headerTop}>
            <div style={S.headerLeft}>
              <AssignmentTurnedInIcon
                style={{
                  fontSize: 28,
                  color: PRIMARY,
                  flexShrink: 0,
                }}
              />

              <div style={S.titleWrap}>
                <h1 style={S.title}>
                  Reporte de Distribución
                </h1>

                <p style={S.subtitle}>
                  {activeMonthLabel}
                </p>
              </div>
            </div>

            <div style={S.headerActions}>
              <label
                className="dist-checkbox"
                style={S.checkboxWrap}
              >
                <input
                  type="checkbox"
                  checked={
                    includeComments
                  }
                  onChange={(e) =>
                    setIncludeComments(
                      e.target.checked
                    )
                  }
                />

                Incluir observaciones
              </label>

              <button
                className="export-btn"
                style={S.exportBtn}
                onClick={handleExport}
              >
                <FileDownloadIcon
                  style={{
                    fontSize: 16,
                  }}
                />

                Exportar Excel
              </button>
            </div>
          </div>

          <div style={S.filterRow}>
            <select
              className="fil-sel"
              style={S.select}
              value={filterByMonth}
              onChange={(e) =>
                setFilterByMonth(
                  e.target.value
                )
              }
            >
              <option value="">
                Todos los meses
              </option>

              {monthOptions.map((o) => (
                <option
                  key={o.value}
                  value={o.value}
                >
                  {o.label}
                </option>
              ))}
            </select>

            <select
              className="fil-sel"
              style={S.select}
              value={filterByClient}
              onChange={(e) =>
                setFilterByClient(
                  e.target.value
                )
              }
            >
              <option value="">
                Todos los clientes
              </option>

              {uniqueClients.map(
                (c) => (
                  <option
                    key={c._id}
                    value={c._id}
                  >
                    {c.name}
                  </option>
                )
              )}
            </select>

            <select
              className="fil-sel"
              style={S.select}
              value={filterByZone}
              onChange={(e) =>
                setFilterByZone(
                  e.target.value
                )
              }
            >
              <option value="">
                Todas las zonas
              </option>

              {zones.map((z) => (
                <option
                  key={z}
                  value={z}
                >
                  {z}
                </option>
              ))}
            </select>

            {hasFilters && (
              <button
                className="clear-btn"
                style={S.clearBtn}
                title="Limpiar filtros"
                onClick={() => {
                  setFilterByClient("");
                  setFilterByZone("");
                  setFilterByMonth("");
                }}
              >
                <ClearAllIcon
                  style={{
                    fontSize: 18,
                  }}
                />
              </button>
            )}

            <span style={S.filterCount}>
              {totalReports} registro
              {totalReports !== 1
                ? "s"
                : ""}
            </span>
          </div>
        </div>

        {/* TABLE */}
        <div style={S.tableSection}>

      {groupedData.length === 0 ? (
        <div style={S.tableWrap}>
          <div style={S.empty}>
            <div
              style={{
                fontSize: 52,
                marginBottom: 12,
                opacity: 0.35,
              }}
            >
              📋
            </div>

            <div
              style={{
                fontWeight: 800,
                color: "#64748b",
                marginBottom: 4,
              }}
            >
              Sin registros
            </div>

            <div style={{ fontSize: 13 }}>
              No hay servicios en el
              periodo seleccionado.
            </div>
          </div>
        </div>
      ) : (
        <div style={S.tableWrap}>
          <div className="dist-scroll-hint">
            Desliza horizontalmente para ver todas las columnas
          </div>
          <div style={S.scroll}>
            <table
              className="dist-report-table"
              style={S.table}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      ...S.th,
                      ...S.thBranch,
                    }}
                  >
                    Nombre de Tienda
                  </th>

                  <th
                    style={{
                      ...S.th,
                      ...S.tdCompany,
                    }}
                  >
                    Empresa
                  </th>

                  <th
                    style={{
                      ...S.th,
                      ...S.tdTime,
                    }}
                  >
                    Entrada
                  </th>

                  <th
                    style={{
                      ...S.th,
                      ...S.tdTime,
                    }}
                  >
                    Salida
                  </th>

                  <th
                    style={{
                      ...S.th,
                      ...S.tdTime,
                    }}
                  >
                    Total
                  </th>

                  <th
                    style={{
                      ...S.th,
                      width: 80,
                      minWidth: 80,
                      maxWidth: 80,
                    }}
                  >
                    Ciudad
                  </th>

                  <th
                    style={{
                      ...S.th,
                      width: 100,
                      minWidth: 100,
                      maxWidth: 100,
                    }}
                  >
                    Técnico
                  </th>

                  {mipColCount > 0 &&
                    Array.from({
                      length:
                        mipColCount,
                    }).map(
                      (_, i) => (
                        <th
                          key={i}
                          style={{
                            ...S.th,
                            ...S.thCompact,
                          }}
                        >
                          MIP{" "}
                          {mipColCount >
                          1
                            ? i + 1
                            : ""}
                        </th>
                      )
                    )}

                  {activeSupportCols.map(
                    (col) => (
                      <th
                        key={col.key}
                        style={{
                          ...S.th,
                          ...S.thCompact,
                        }}
                      >
                        {col.label}
                      </th>
                    )
                  )}

                  <th
                    style={{
                      ...S.th,
                      ...S.tdNotes,
                    }}
                  >
                    Observaciones
                  </th>
                </tr>
              </thead>

              <tbody>
                {groupedData.map(
                  (row, i) => {
                    const rowBg =
                      i % 2 === 0
                        ? "#fff"
                        : "#fafbfc";

                    return (
                      <tr
                        key={
                          row.rowKey
                        }
                        className="tr-row"
                        style={{
                          background:
                            rowBg,
                        }}
                      >
                        <td
                          style={{
                            ...S.tdBranch,
                            background:
                              rowBg,
                          }}
                        >
                          {row.branchName.toUpperCase()}
                        </td>

                        <td
                          style={{
                            ...S.td,
                            ...S.tdCompany,
                          }}
                        >
                          {
                            row.clientName
                          }
                        </td>

                        <td
                          style={{
                            ...S.td,
                            ...S.tdTime,
                          }}
                        >
                          {row.entryTime ||
                            "—"}
                        </td>

                        <td
                          style={{
                            ...S.td,
                            ...S.tdTime,
                          }}
                        >
                          {row.exitTime ||
                            "—"}
                        </td>

                        <td
                          style={{
                            ...S.td,
                            ...S.tdTime,
                            fontWeight: 700,
                          }}
                        >
                          {
                            row.totalHours
                          }
                        </td>

                        <td
                          style={{
                            ...S.td,
                            width: 80,
                            minWidth: 80,
                            maxWidth: 80,
                          }}
                        >
                          {row.city}
                        </td>

                        <td
                          style={{
                            ...S.td,
                            width: 100,
                            minWidth: 100,
                            maxWidth: 100,
                          }}
                        >
                          {
                            row.technician
                          }
                        </td>

                        {mipColCount >
                          0 &&
                          Array.from({
                            length:
                              mipColCount,
                          }).map(
                            (
                              _,
                              idx
                            ) => {
                              const dateStr =
                                row
                                  .mipDates[
                                  idx
                                ] ||
                                null;

                              return (
                                <td
                                  key={
                                    idx
                                  }
                                  style={{
                                    ...S.td,
                                    ...S.tdCompact,
                                  }}
                                >
                                  {dateStr ? (
                                    <span
                                      style={mipBadge(
                                        dateStr
                                      )}
                                    >
                                      {
                                        dateStr
                                      }
                                    </span>
                                  ) : (
                                    "—"
                                  )}
                                </td>
                              );
                            }
                          )}

                        {activeSupportCols.map(
                          (
                            col
                          ) => {
                            const dateStr =
                              row
                                .typeFirstDate[
                                col
                                  .key
                              ] ||
                              null;

                            return (
                              <td
                                key={
                                  col.key
                                }
                                style={{
                                  ...S.td,
                                  ...S.tdCompact,
                                }}
                              >
                                {dateStr ? (
                                  <span
                                    style={dateBadge(
                                      dateStr
                                    )}
                                  >
                                    {
                                      dateStr
                                    }
                                  </span>
                                ) : (
                                  "—"
                                )}
                              </td>
                            );
                          }
                        )}

                        <td
                          style={{
                            ...S.td,
                            ...S.tdNotes,
                          }}
                        >
                          {row.notes ||
                            "—"}
                        </td>
                      </tr>
                    );
                  }
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
}